/**
 * Customer card → "Dokumentai": the category tree the file list is browsed by.
 *
 * A flat list of every file ever filed against a customer is unreadable once
 * there are more than a screenful of them, so the tab shows the same category
 * structure as the Documents page — pruned to the branches this customer
 * actually has files in. A branch with no files is never rendered.
 *
 * Everything here is derived client-side from what the tab already loads: the
 * files (each one carries its own categories) plus the company's flat category
 * list, which supplies the titles of the parent categories that hold no files
 * of their own.
 */

/** The pseudo-node for files filed against the customer with no category. */
export const UNCATEGORIZED_NODE_ID = "__uncategorized__";
/** The pseudo-node that selects every file, i.e. no category filter. */
export const ALL_NODE_ID = "";

export interface FlatCategory {
  id?: string | null;
  title?: string | null;
  parentCategory?: string | { id?: string | null } | null;
  order?: string | number | null;
}

export interface TreeFile {
  id: string;
  categories?: Array<FlatCategory | null> | null;
}

export interface DocumentTreeNode {
  id: string;
  title: string;
  /** Files filed directly in this category. */
  ownCount: number;
  /** Distinct files in this category and everything below it. */
  count: number;
  /** Distinct file ids in this category and everything below it. */
  fileIds: string[];
  children: DocumentTreeNode[];
}

export interface DocumentTree {
  nodes: DocumentTreeNode[];
  uncategorizedFileIds: string[];
  /** Distinct files in the whole tree, including the uncategorized ones. */
  total: number;
}

const parentIdOf = (category: FlatCategory): string | null => {
  const parent = category.parentCategory;
  if (!parent) return null;
  if (typeof parent === "string") return parent || null;
  return parent.id || null;
};

const NUMERIC_PREFIX = /^(\d+(?:\.\d+)*)/;

/**
 * Same ordering the Documents page shows: "2.10" after "2.9", numbered
 * categories before named ones, the rest alphabetically.
 */
const compareTitles = (a: string, b: string): number => {
  const matchA = NUMERIC_PREFIX.exec(a);
  const matchB = NUMERIC_PREFIX.exec(b);
  if (matchA && !matchB) return -1;
  if (!matchA && matchB) return 1;
  if (matchA && matchB) {
    const partsA = matchA[1].split(".").map((part) => parseInt(part, 10));
    const partsB = matchB[1].split(".").map((part) => parseInt(part, 10));
    for (let i = 0; i < Math.max(partsA.length, partsB.length); i += 1) {
      const diff = (partsA[i] || 0) - (partsB[i] || 0);
      if (diff !== 0) return diff;
    }
  }
  return a.localeCompare(b, "en", { sensitivity: "base" });
};

const compareCategories = (a: FlatCategory, b: FlatCategory): number => {
  const orderA = Number(a.order);
  const orderB = Number(b.order);
  if (Number.isFinite(orderA) && Number.isFinite(orderB) && orderA !== orderB) {
    return orderA - orderB;
  }
  return compareTitles(a.title || "", b.title || "");
};

export function buildDocumentTree(
  files: TreeFile[],
  categories: FlatCategory[]
): DocumentTree {
  const safeFiles = Array.isArray(files) ? files.filter(Boolean) : [];

  // Every category we know of: the company's list, plus any category a file
  // carries that the list didn't return (a shared category, or one the list
  // request hasn't answered yet — the files alone already build a usable tree).
  const categoryById = new Map<string, FlatCategory>();
  const remember = (category: FlatCategory | null | undefined) => {
    if (!category || !category.id) return;
    if (!categoryById.has(category.id)) categoryById.set(category.id, category);
  };
  (Array.isArray(categories) ? categories : []).forEach(remember);
  safeFiles.forEach((file) => (file.categories || []).forEach(remember));

  // categoryId -> ids of the files filed directly in it.
  const directFileIds = new Map<string, string[]>();
  const uncategorizedFileIds: string[] = [];

  safeFiles.forEach((file) => {
    if (!file.id) return;
    const ids = Array.from(
      new Set(
        (file.categories || [])
          .map((category) => category?.id)
          .filter((id): id is string => Boolean(id) && categoryById.has(id!))
      )
    );
    if (ids.length === 0) {
      uncategorizedFileIds.push(file.id);
      return;
    }
    ids.forEach((id) => {
      const bucket = directFileIds.get(id);
      if (bucket) bucket.push(file.id);
      else directFileIds.set(id, [file.id]);
    });
  });

  // Keep the categories that hold files and every ancestor above them, so a
  // branch is rendered whole — but only where there is something to show.
  const kept = new Set<string>();
  directFileIds.forEach((_fileIds, categoryId) => {
    let current: string | null = categoryId;
    const seen = new Set<string>();
    while (current && categoryById.has(current) && !seen.has(current)) {
      seen.add(current);
      kept.add(current);
      current = parentIdOf(categoryById.get(current) as FlatCategory);
    }
  });

  // A kept category whose parent is unknown (deleted, or outside the returned
  // list) is rendered as a root, so its files never fall out of the tree.
  const childrenByParent = new Map<string | null, FlatCategory[]>();
  kept.forEach((categoryId) => {
    const category = categoryById.get(categoryId) as FlatCategory;
    const parent = parentIdOf(category);
    const key = parent && kept.has(parent) ? parent : null;
    const bucket = childrenByParent.get(key);
    if (bucket) bucket.push(category);
    else childrenByParent.set(key, [category]);
  });

  const built = new Set<string>();

  const buildNode = (category: FlatCategory): DocumentTreeNode => {
    const id = category.id as string;
    built.add(id);
    const children = (childrenByParent.get(id) || [])
      // Guard against a parentCategory cycle in the data: a category already
      // on this path is not built again.
      .filter((child) => child.id && !built.has(child.id))
      .sort(compareCategories)
      .map(buildNode);

    const ownFileIds = directFileIds.get(id) || [];
    const fileIds = Array.from(
      new Set([...ownFileIds, ...children.flatMap((child) => child.fileIds)])
    );

    return {
      id,
      title: category.title || "",
      ownCount: ownFileIds.length,
      count: fileIds.length,
      fileIds,
      children,
    };
  };

  const nodes = (childrenByParent.get(null) || [])
    .sort(compareCategories)
    .map(buildNode);

  // A parentCategory cycle in the data would leave a group with no root at all
  // (every member has a kept parent), and its files would silently disappear
  // from the tree. Whatever is left unbuilt is rendered as a root instead.
  Array.from(kept)
    .filter((id) => !built.has(id))
    .map((id) => categoryById.get(id) as FlatCategory)
    .sort(compareCategories)
    .forEach((category) => {
      if (!built.has(category.id as string)) nodes.push(buildNode(category));
    });

  const categorizedCount = new Set(nodes.flatMap((node) => node.fileIds)).size;

  return {
    nodes,
    uncategorizedFileIds,
    total: categorizedCount + uncategorizedFileIds.length,
  };
}

/** Depth-first walk of the built tree, parents before children. */
export function flattenTree(
  nodes: DocumentTreeNode[]
): Array<{ node: DocumentTreeNode; depth: number }> {
  const out: Array<{ node: DocumentTreeNode; depth: number }> = [];
  const walk = (list: DocumentTreeNode[], depth: number) => {
    list.forEach((node) => {
      out.push({ node, depth });
      walk(node.children, depth + 1);
    });
  };
  walk(nodes, 0);
  return out;
}

/** The node with this id, or null when it is no longer in the tree. */
export function findNode(
  nodes: DocumentTreeNode[],
  id: string
): DocumentTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}

/** Ids of the categories on the path down to `id`, excluding `id` itself. */
export function findAncestorIds(
  nodes: DocumentTreeNode[],
  id: string,
  path: string[] = []
): string[] | null {
  for (const node of nodes) {
    if (node.id === id) return path;
    const found = findAncestorIds(node.children, id, [...path, node.id]);
    if (found) return found;
  }
  return null;
}
